import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { toast } from 'sonner';
import api from '../utils/api';
import {
  MessageCircle,
  Link2,
  Unlink,
  Bell,
  BellOff,
  ExternalLink,
  Loader2,
  Send,
  CheckCircle2,
} from 'lucide-react';

const TelegramSettings = () => {
  const [status, setStatus] = useState({
    connected: false,
    chat_id: null,
    notifications_enabled: true,
    bot_configured: false
  });
  const [loading, setLoading] = useState(true);
  const [verificationCode, setVerificationCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);

  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    try {
      const response = await api.get('/telegram/status');
      setStatus(response.data);
    } catch (error) {
      console.error('Failed to load Telegram status');
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error('Please enter the 6-digit code from the bot');
      return;
    }

    setVerifying(true);
    try {
      const response = await api.post('/telegram/verify-from-bot', { code: verificationCode });
      toast.success('Telegram connected successfully!');
      setStatus(prev => ({ ...prev, connected: true }));
      setVerificationCode('');
      loadStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid or expired code');
    } finally {
      setVerifying(false);
    }
  };

  const disconnect = async () => {
    try {
      await api.post('/telegram/disconnect');
      setStatus(prev => ({ ...prev, connected: false, chat_id: null }));
      setVerificationCode('');
      toast.success('Telegram disconnected');
    } catch (error) {
      toast.error('Failed to disconnect');
    }
  };

  const toggleNotifications = async () => {
    try {
      const response = await api.post('/telegram/toggle-notifications', null, {
        params: { enabled: !status.notifications_enabled }
      });
      setStatus(prev => ({ ...prev, notifications_enabled: response.data.telegram_notifications }));
      toast.success(`Notifications ${response.data.telegram_notifications ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error('Failed to update notification settings');
    }
  };

  const sendTestNotification = async () => {
    setSendingTest(true);
    try {
      await api.post('/telegram/test');
      toast.success('Test notification sent! Check your Telegram.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test');
    } finally {
      setSendingTest(false);
    }
  };

  if (loading) {
    return (
      <Card className="bg-[#15171C] border-[#1E293B]">
        <CardContent className="py-8 flex justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-[#2F6BFF]" />
        </CardContent>
      </Card>
    );
  }

  if (!status.bot_configured) {
    return (
      <Card className="bg-[#15171C] border-[#1E293B]">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-[#2F6BFF]/20">
              <MessageCircle className="w-5 h-5 text-[#2F6BFF]" />
            </div>
            <div>
              <CardTitle className="font-['Outfit']">Telegram Alerts</CardTitle>
              <CardDescription className="text-[#94A3B8]">
                Get real-time trade notifications on Telegram
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
            <p className="text-sm text-yellow-400">
              Telegram bot is not configured yet. Coming soon!
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-[#15171C] border-[#1E293B]">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-[#2F6BFF]/20">
              <MessageCircle className="w-5 h-5 text-[#2F6BFF]" />
            </div>
            <div>
              <CardTitle className="font-['Outfit']">Telegram Alerts</CardTitle>
              <CardDescription className="text-[#94A3B8]">
                Get real-time trade notifications
              </CardDescription>
            </div>
          </div>
          <Badge className={status.connected ? 'bg-green-500' : 'bg-[#1E293B]'}>
            {status.connected ? 'Connected' : 'Not Connected'}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {!status.connected ? (
          // Connection flow
          <div className="space-y-4">
            <div className="p-4 bg-[#0B0D12] rounded-lg border border-[#1E293B]">
              <h4 className="font-semibold mb-3">How to connect:</h4>
              <ol className="space-y-2 text-sm text-[#94A3B8]">
                <li className="flex gap-2">
                  <span className="flex-shrink-0 w-5 h-5 bg-[#2F6BFF] rounded-full flex items-center justify-center text-xs">1</span>
                  <span>Open Telegram and search for <strong className="text-[#F5F7FA]">@ApexionUK_bot</strong></span>
                </li>
                <li className="flex gap-2">
                  <span className="flex-shrink-0 w-5 h-5 bg-[#2F6BFF] rounded-full flex items-center justify-center text-xs">2</span>
                  <span>Send <strong className="text-[#F5F7FA]">/start</strong> to the bot</span>
                </li>
                <li className="flex gap-2">
                  <span className="flex-shrink-0 w-5 h-5 bg-[#2F6BFF] rounded-full flex items-center justify-center text-xs">3</span>
                  <span>Enter the <strong className="text-[#F5F7FA]">6-digit code</strong> the bot gives you below</span>
                </li>
              </ol>
            </div>

            <Button
              variant="outline"
              className="w-full border-[#1E293B]"
              onClick={() => window.open('https://t.me/ApexionUK_bot', '_blank')}
            >
              Open Telegram Bot <ExternalLink className="w-4 h-4 ml-2" />
            </Button>

            <div className="p-4 bg-[#2F6BFF]/10 border border-[#2F6BFF]/30 rounded-lg">
              <p className="text-sm text-[#94A3B8] mb-3">Enter the code from the bot:</p>
              <div className="flex gap-3">
                <Input
                  type="text"
                  placeholder="Enter 6-digit code"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="text-center text-xl font-bold font-['JetBrains_Mono'] tracking-wider bg-[#0B0D12] border-[#1E293B]"
                  maxLength={6}
                />
                <Button
                  onClick={verifyCode}
                  disabled={verifying || !verificationCode || verificationCode.length !== 6}
                  className="bg-[#2F6BFF] px-6"
                >
                  {verifying ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <CheckCircle2 className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>
          </div>
        ) : (
          // Connected state
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-[#0B0D12] rounded-lg">
              <div className="flex items-center gap-3">
                {status.notifications_enabled ? (
                  <Bell className="w-5 h-5 text-green-400" />
                ) : (
                  <BellOff className="w-5 h-5 text-[#94A3B8]" />
                )}
                <div>
                  <Label className="text-[#F5F7FA]">Trade Notifications</Label>
                  <p className="text-xs text-[#64748B]">Receive alerts when trades execute</p>
                </div>
              </div>
              <Switch
                checked={status.notifications_enabled}
                onCheckedChange={toggleNotifications}
              />
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={sendTestNotification}
                disabled={sendingTest}
                className="flex-1 border-[#1E293B]"
              >
                {sendingTest ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Send Test
              </Button>
              <Button
                variant="outline"
                onClick={disconnect}
                className="flex-1 border-red-500/50 text-red-400 hover:bg-red-500/10"
              >
                <Unlink className="w-4 h-4 mr-2" />
                Disconnect
              </Button>
            </div>

            <p className="text-xs text-[#64748B] text-center">
              You'll receive alerts for: trades, bot status changes, and daily summaries
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TelegramSettings;
